/**
 * Process-level safety net for the `serve` entrypoint.
 *
 * `serve` treats `uncaughtException` / `unhandledRejection` as non-fatal: a
 * stray watcher event should not take a live session down. The exception is a
 * dead output pipe, where logging the failure re-triggers it.
 * See `_devlog/notes/2026-09-07-stdout-epipe-log-loop.md`.
 */
import {
  isBrokenPipeError,
  isUnwritableStreamError,
  markStreamBroken,
} from "@skaile/workspaces/core/logging";

/**
 * Exit status for "our output pipe is gone". 128 + SIGPIPE(13), the shell
 * convention for death by broken pipe, so a supervisor can tell this apart
 * from an ordinary crash.
 */
export const BROKEN_PIPE_EXIT_CODE = 141;

/** Structural subset of `process.stdout` the safety net inspects and watches. */
type StdioStream = {
  destroyed?: boolean;
  writableEnded?: boolean;
  on?: (event: "error", listener: (err: unknown) => void) => unknown;
  off?: (event: "error", listener: (err: unknown) => void) => unknown;
};

export interface SafetyNetOptions {
  /** Emits the non-fatal diagnostic line. Routed through the LogStore. */
  log: (line: string) => void;
  /** Terminates the process. Defaults to `process.exit`. */
  exit?: (code: number) => void;
  /** Stream whose death makes logging pointless. Defaults to `process.stdout`. */
  stdout?: StdioStream;
  /**
   * Exit when the output pipe dies. Default `true` here, but the caller
   * decides: only code that owns the process should pass `true`. See
   * `ServeOptions.exitOnBrokenPipe`, which defaults to `false` for that
   * reason. Silencing the sinks is unaffected either way.
   */
  exitOnBrokenPipe?: boolean;
}

export type SafetyNetHandler = (
  label: "uncaughtException" | "unhandledRejection",
  err: unknown,
) => void;

/** Apply the defaults once, so the two entry points cannot drift apart. */
function resolveOptions(opts: SafetyNetOptions) {
  return {
    log: opts.log,
    exit: opts.exit ?? ((code: number) => process.exit(code)),
    stdout: opts.stdout ?? process.stdout,
    exitOnBrokenPipe: opts.exitOnBrokenPipe ?? true,
  };
}

/**
 * Build the handler shared by both process-level listeners.
 *
 * Exposed separately from {@link installProcessSafetyNet} so its policy can be
 * tested without registering real listeners on the test runner's process.
 */
export function createSafetyNetHandler(opts: SafetyNetOptions): SafetyNetHandler {
  const { exit, stdout, exitOnBrokenPipe } = resolveOptions(opts);
  let handling = false;

  const outputIsGone = (): boolean => stdout.destroyed === true || stdout.writableEnded === true;

  return (label, err) => {
    // Re-entrancy: this call was raised by the `opts.log` of an outer call.
    // Logging it would recurse; the outer frame decides what happens next.
    if (handling) return;

    // Nowhere left to write, and no reason to keep serving a session whose
    // output channel is dead. Exit before touching the log sink.
    if (exitOnBrokenPipe && (isBrokenPipeError(err) || outputIsGone())) {
      exit(BROKEN_PIPE_EXIT_CODE);
      return;
    }

    // Not exiting, so still record it. A dead stdout says nothing about the
    // SQLite and WebSocket sinks, and a child running unattended for hours
    // must not drop every uncaught exception just because the CLI that
    // spawned it has gone. The re-entrancy guard keeps this non-recursive.
    handling = true;
    try {
      opts.log(`[serve] ${label} (non-fatal): ${render(err)}`);
    } catch (logErr) {
      // The sink itself failed. Do not report that through the sink.
      if (exitOnBrokenPipe && (isBrokenPipeError(logErr) || outputIsGone())) {
        exit(BROKEN_PIPE_EXIT_CODE);
      }
    } finally {
      handling = false;
    }
  };
}

/**
 * Live watches per stream. Ref-counted and keyed by stream because the target
 * is normally the process-global `process.stdout`: an embedder that starts
 * several servers — or retries a failed start, whose teardown never runs —
 * would otherwise stack a listener per attempt. The first caller's `exit` and
 * `exitOnBrokenPipe` win for as long as the watch is live.
 */
const activeWatches = new WeakMap<object, { count: number; remove: () => void }>();

/**
 * Claim the output stream's `error` event. Returns an uninstall function.
 *
 * This is its sole owner — sinks stay passive readers of the shared
 * broken-stream mark, because claiming the event suppresses Node's fatal-EPIPE
 * exit and a CLI piped into `head` still needs it. Claiming it here is what
 * keeps the failure out of `uncaughtException`, and so out of the log sink
 * that raised it. Install as early as there is a stream to watch: it carries
 * the whole broken-pipe guarantee on its own.
 */
export function watchOutputStream(opts: Omit<SafetyNetOptions, "log">): () => void {
  const { exit, stdout, exitOnBrokenPipe } = resolveOptions({ log: () => {}, ...opts });
  const key = stdout as object;

  const existing = activeWatches.get(key);
  if (existing) {
    existing.count++;
    return releaseOnce(key);
  }

  // Marking the stream tells every sink to go quiet, exit or no exit.
  const onStdoutError = (err: unknown) => {
    if (isUnwritableStreamError(err)) markStreamBroken(key);
    if (exitOnBrokenPipe && isBrokenPipeError(err)) exit(BROKEN_PIPE_EXIT_CODE);
  };

  stdout.on?.("error", onStdoutError);
  activeWatches.set(key, {
    count: 1,
    remove: () => stdout.off?.("error", onStdoutError),
  });
  return releaseOnce(key);
}

/**
 * One uninstall per holder. Without the latch a caller releasing twice spends
 * another holder's reference and tears down a watch still in use — `serve`'s
 * `session_init` bail-out is reachable more than once per server.
 */
function releaseOnce(key: object): () => void {
  let released = false;
  return () => {
    if (released) return;
    released = true;
    release(key);
  };
}

function release(key: object): void {
  const watch = activeWatches.get(key);
  if (!watch) return;
  watch.count--;
  if (watch.count > 0) return;
  watch.remove();
  activeWatches.delete(key);
}

/**
 * Register the `uncaughtException` / `unhandledRejection` listeners, which
 * downgrade a stray async error from fatal to logged. Returns an uninstall
 * function; call it when the server shuts down, or an in-process host that
 * starts several servers accumulates listener sets.
 *
 * Install this only once startup has succeeded. Downgrading during boot means
 * a failure before the transport listens no longer stops the process, and it
 * would go on reporting healthy (the process targets check liveness by pid)
 * for a server that never came up.
 */
export function installProcessSafetyNet(opts: SafetyNetOptions): () => void {
  const handle = createSafetyNetHandler(opts);
  const onException = (err: unknown) => handle("uncaughtException", err);
  const onRejection = (reason: unknown) => handle("unhandledRejection", reason);

  process.on("uncaughtException", onException);
  process.on("unhandledRejection", onRejection);

  return () => {
    process.off("uncaughtException", onException);
    process.off("unhandledRejection", onRejection);
  };
}

function render(err: unknown): string {
  if (err instanceof Error) return err.stack ?? err.message;
  return String(err);
}
